import { API_BASE_URL } from "../constants";

/**
 * Member shape from Stytch's organizations.members.search response — only the
 * fields the admin console table actually consumes.
 *
 * `level` is the member's effective tier, collapsed server-side from their
 * Stytch roles by `levelFromRoles` — highest held role wins, and the
 * implicit `stytch_member` role everyone carries is the fall-through rather
 * than a positive match. The role badge reads this directly.
 *
 * `status` strings come from Stytch: 'active' for accepted members, 'invited'
 * / 'pending' for unaccepted invites. Anything-not-'active' renders as the
 * "Pending" pill.
 */
export type PermissionLevel = "member" | "manager" | "admin";

/** Stytch Role IDs this console assigns. `maru_manager` must match MANAGER_ROLE_ID server-side. */
export type StytchRoleId = "stytch_admin" | "maru_manager" | "stytch_member";

/**
 * The tier <-> Stytch Role ID mapping, in one place because both the row's
 * action menu and the confirmation modal need to translate between them.
 *
 * Assigning any of these REPLACES the member's roles rather than adding to
 * them (Stytch `roles` is a full replacement), so each entry sets a tier
 * outright.
 */
export const ROLE_FOR_LEVEL: Record<PermissionLevel, StytchRoleId> = {
  admin: "stytch_admin",
  manager: "maru_manager",
  member: "stytch_member",
};

export const LEVEL_FOR_ROLE: Record<StytchRoleId, PermissionLevel> = {
  stytch_admin: "admin",
  maru_manager: "manager",
  stytch_member: "member",
};

export const LEVEL_LABEL: Record<PermissionLevel, string> = {
  admin: "Admin",
  manager: "Manager",
  member: "Member",
};

/** Ordered high to low, so an action menu reads top-down as a demotion path. */
export const ALL_LEVELS: PermissionLevel[] = ["admin", "manager", "member"];

export interface Member {
  member_id: string;
  email_address: string;
  name: string;
  status: string;
  level: PermissionLevel;
  organization_id: string;
}

/**
 * Envelope returned by GET /admin/members. Mirrors Stytch's searchMembers
 * response shape (only the fields the UI uses).
 *
 * `organizations` maps organization_id -> organization_name for every org in
 * the project, so the table can render an Organization column without an
 * extra call per member. The listing itself now spans every org (admins
 * administer the whole project, not just their own).
 */
export interface ListMembersResponse {
  members: Member[];
  results_metadata: {
    total: number;
    next_cursor?: string;
  };
  organizations: Record<string, string>;
}

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * GET /admin/members?cursor=&query=
 *
 * Server-side cursor pagination + free-text email search.
 */
export async function listMembers(
  cursor: string | undefined,
  query: string | undefined,
  token?: string,
  signal?: AbortSignal,
): Promise<ListMembersResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (query) params.set("query", query);
  const qs = params.toString();
  const url = qs
    ? `${API_BASE_URL}/admin/members?${qs}`
    : `${API_BASE_URL}/admin/members`;
  const response = await fetch(url, {
    headers: authHeaders(token),
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to list members: ${await response.text()}`);
  }
  return response.json();
}

/**
 * POST /admin/members
 *
 * Invite a new member with role at invite time. The backend
 * (AdminService.inviteMember) translates this into
 * stytchClient.magicLinks.email.invite.
 */
export async function inviteMember(
  email: string,
  role: string,
  token?: string,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/admin/members`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) {
    throw new Error(`Failed to send invite: ${await response.text()}`);
  }
}

/**
 * PATCH /admin/members/:memberId
 *
 * Flip a member's role between stytch_admin and stytch_member. The role
 * propagates to the member's UI on their next session refresh. The backend
 * (AdminService.updateMemberRole) enforces self-protection; the UI also hides
 * the action on the admin's own row as a UX nicety.
 *
 * `organizationId` must be the target row's own org (from the member's
 * `organization_id` field). The admin listing now spans every organization,
 * so omitting it would fall back to the caller's own org server-side and
 * silently miss a member who belongs to a different one.
 */
export async function updateMemberRole(
  memberId: string,
  role: string,
  organizationId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/admin/members/${encodeURIComponent(memberId)}`,
    {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify({ role, organizationId }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to change role: ${await response.text()}`);
  }
}

/**
 * DELETE /admin/members/:memberId
 *
 * Deactivate an active member OR cancel a pending invite — same Stytch call
 * either way per the SDK JSDoc on magicLinks.email.invite. The backend
 * (AdminService.deleteMember) enforces self-protection.
 *
 * `organizationId` must be the target row's own org — see the note on
 * `updateMemberRole` above; the same cross-org fallback bug applies here.
 */
export async function deleteMember(
  memberId: string,
  organizationId: string,
  token?: string,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/admin/members/${encodeURIComponent(memberId)}`,
    {
      method: "DELETE",
      headers: authHeaders(token),
      body: JSON.stringify({ organizationId }),
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to deactivate member: ${await response.text()}`);
  }
}
